<template>
  <div
    :id="modalId"
    ref="modalRef"
    tabindex="-1"
    aria-hidden="true"
    class="fixed inset-0 flex items-start justify-center px-4 pt-10 pb-8 modal-cont overflow-y-auto"
  >
    <div :class="['relative w-full', modalSize]">
      <div
        class="relative bg-white rounded-lg shadow overflow-hidden dark:bg-gray-700"
      >
        <div
          v-if="$slots.header || title"
          class="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b rounded-t dark:border-gray-600"
        >
          <div class="flex-1 min-w-0">
            <slot name="header">
              <h3
                class="text-xl font-medium text-gray-900 dark:text-white"
                data-cy="modal-title"
              >
                {{ title }}
              </h3>
            </slot>
          </div>
          <button
            v-if="showCloseButton"
            type="button"
            class="text-gray-400 bg-transparent rounded-lg text-sm p-1.5 inline-flex items-center hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-600 dark:hover:text-white"
            data-cy="modal-close-btn"
            @click="closeModal"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="sr-only">Close modal</span>
          </button>
        </div>
        <div :class="bodyClasses">
          <slot />
        </div>
        <div
          v-if="$slots.footer"
          class="border-t dark:border-gray-600 rounded-b"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type InstanceOptions,
  type ModalInterface,
  type ModalOptions,
  Modal
} from "flowbite";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

export type ModalSize = "small" | "medium" | "large" | "extralarge";
export type ModalBodyPadding = "default" | "compact" | "none";

const props = withDefaults(
  defineProps<{
    size?: ModalSize;
    title?: string;
    showCloseButton?: boolean;
    staticBackdrop?: boolean;
    modelValue?: boolean;
    bodyPadding?: ModalBodyPadding;
  }>(),
  {
    size: "medium",
    title: "",
    showCloseButton: false,
    staticBackdrop: false,
    modelValue: false,
    bodyPadding: "default"
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "show"): void;
  (e: "hide"): void;
}>();

const modalRef = ref<HTMLElement | null>(null);
const modalId = `seranova-modal-${Math.random().toString(36).slice(2, 10)}`;
let modal: ModalInterface | null = null;

const modalSize = computed(
  () =>
    ({
      small: "max-w-[416px]",
      medium: "max-w-[640px]",
      large: "max-w-[864px]",
      extralarge: "max-w-[1248px]"
    }[props.size] ?? "max-w-[640px]")
);

const bodyClasses = computed(() => {
  if (props.bodyPadding === "none") return "";
  if (props.bodyPadding === "compact") return "p-3 md:p-4";
  return "p-4 md:p-5";
});

onMounted(() => {
  if (!modalRef.value) return;
  const modalOptions: ModalOptions = {
    placement: "top-center",
    backdrop: props.staticBackdrop ? "static" : "dynamic",
    backdropClasses:
      "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 seranova-modal-backdrop",
    closable: true,
    onHide: () => emit("update:modelValue", false),
    onShow: () => emit("show"),
    onHidden: () => emit("hide")
  };
  const instanceOptions: InstanceOptions = { id: modalId, override: true };
  modal = new Modal(modalRef.value, modalOptions, instanceOptions);

  watch(
    () => props.modelValue,
    (newValue) => {
      if (!modal) return;
      if (newValue) modal.show();
      else modal.hide();
    },
    { immediate: true }
  );
});

onBeforeUnmount(() => {
  // Flowbite leaves its own backdrop behind if the host component unmounts
  // mid-open. Only sweep our own class to avoid clobbering Bootstrap modals.
  if (modal) modal.hide();
  document
    .querySelectorAll(".seranova-modal-backdrop")
    .forEach((el) => el.parentNode?.removeChild(el));
});

const closeModal = () => {
  if (modal) modal.hide();
  emit("update:modelValue", false);
};

defineExpose({ open: () => modal?.show(), close: closeModal });
</script>

<style>
/*
  Sit under Bootstrap's legacy modal stack (.modal = 1050,
  .modal-backdrop = 1040) so legacy ConfirmationModal / global confirms
  can layer on top when invoked from inside a SeranovaModal. The
  seranova-modal-backdrop class is unique to this component (Flowbite
  appends it to <body>) so it doesn't collide with Bootstrap's
  .modal-backdrop selector.
*/
.modal-cont {
  z-index: 1030;
}
.seranova-modal-backdrop {
  z-index: 1020;
}
</style>
